/**
 * CHECKOUT TERMS — what a real-money confirm shows besides the price: the
 * consent line (Terms + Privacy, linked to the CMS documents) and the accepted
 * payment methods. Data and pure helpers only; CheckoutTerms.vue renders them.
 *
 * Nothing here is hardcoded copy: the methods are the ones enabled in Admin →
 * Settings → Payment methods (served on GET /api/site-info, the same list the
 * landing footer shows), the documents are the active CMS legal pages.
 */
import { api } from '../../core/network/api';
import { fetchLegalPages, sortLegalDocs, type LegalPageMeta } from './legal-api';

export interface PaymentMethodBadge { slug: string; name: string; icon: string }

export interface CheckoutTerms { methods: PaymentMethodBadge[]; docs: LegalPageMeta[] }

/** The papers a buyer agrees to, by placeholder in `shop.consent.agree`.
 *  Slugs from the seed (backend/src/game/legal/legal.constants.ts). */
export const CONSENT_DOCS = { terms: 'terms-of-service', privacy: 'privacy-policy' } as const;
export type ConsentDoc = keyof typeof CONSENT_DOCS;

export type ConsentPart = { text: string } | { doc: ConsentDoc };

/** Split a translated consent sentence into text runs and document links.
 *  The caller resolves the placeholders to themselves —
 *  t('shop.consent.agree', { terms: '{terms}', privacy: '{privacy}' }) — so
 *  each locale keeps its own word order around the links. */
export function consentParts(sentence: string): ConsentPart[] {
  return sentence
    .split(/(\{(?:terms|privacy)\})/)
    .filter((s) => s !== '')
    .map((s) => {
      const m = /^\{(terms|privacy)\}$/.exec(s);
      return m ? { doc: m[1] as ConsentDoc } : { text: s };
    });
}

let once: Promise<CheckoutTerms> | null = null;

/** Both lists, fetched once per session. Either half failing yields an empty
 *  list (the row hides, the consent links fall back to translated titles); a
 *  result with nothing in it is not cached, so the next dialog retries. */
export function loadCheckoutTerms(): Promise<CheckoutTerms> {
  once ??= Promise.all([
    api.get<{ paymentMethods?: PaymentMethodBadge[] }>('/site-info')
      .then((d) => d.paymentMethods ?? [])
      .catch((): PaymentMethodBadge[] => []),
    fetchLegalPages().then(sortLegalDocs).catch((): LegalPageMeta[] => []),
  ]).then(([methods, docs]) => {
    if (!methods.length && !docs.length) once = null;
    return { methods, docs };
  });
  return once;
}
